import { useCallback, useState } from 'react';
import { useSetAtom } from 'jotai';
import { DestroyConfirmation } from 'components/destroy-confirmation';
import { deleteVestingDocumentAtom, verifyVestingDocumentAtom } from './atoms';
import { vestingDocumentLabel, type VestingDocumentRow } from './types';

export const useVestingDocumentActions = (onChanged?: () => void) => {
  const verifyAtom = useSetAtom(verifyVestingDocumentAtom);
  const removeAtom = useSetAtom(deleteVestingDocumentAtom);
  const [error, setError] = useState<string | null>(null);
  const [verifyingId, setVerifyingId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<VestingDocumentRow | null>(null);
  const [removing, setRemoving] = useState(false);

  const verify = useCallback(
    (row: VestingDocumentRow) => {
      setError(null);
      setVerifyingId(row.id);
      verifyAtom({
        url: row.href,
        method: 'PATCH',
        onSuccess: () => {
          setVerifyingId(null);
          onChanged?.();
        },
        onError: (message?: string) => {
          setVerifyingId(null);
          setError(message ?? 'Could not verify this vesting document.');
        },
      });
    },
    [verifyAtom, onChanged]
  );

  // A hard delete: the PaperTrail destroy version is its only record, so it waits for the confirmation.
  const remove = useCallback(() => {
    if (!confirming) return;
    setError(null);
    setRemoving(true);
    removeAtom({
      url: confirming.href,
      method: 'DELETE',
      onSuccess: () => {
        setRemoving(false);
        setConfirming(null);
        onChanged?.();
      },
      onError: (message?: string) => {
        setRemoving(false);
        setConfirming(null);
        setError(message ?? 'Could not remove this vesting document.');
      },
    });
  }, [confirming, removeAtom, onChanged]);

  // A draft workspace's row carries `pending`: its removal is staged until promote.
  const staged = !!confirming && 'pending' in confirming;

  return {
    error,
    clearError: () => setError(null),
    verify,
    verifyingId,
    askRemove: setConfirming,
    confirmation: confirming && (
      <DestroyConfirmation
        open
        modalHeading="Remove Vesting Document"
        size="sm"
        primaryButtonDisabled={removing}
        // A hard delete with no undo must not be dismissed by a stray click outside.
        preventCloseOnClickOutside
        onRequestClose={() => {
          if (!removing) setConfirming(null);
        }}
        onRequestSubmit={remove}
      >
        <p>
          Are you sure you want to remove{' '}
          <strong>{vestingDocumentLabel(confirming)}</strong> as a vesting
          document of this tract?{' '}
          {staged
            ? 'The removal is staged in this workspace and can be undone until it is promoted.'
            : 'This action cannot be undone.'}
        </p>
      </DestroyConfirmation>
    ),
  };
};
